// Kata Coaching service for KATA Dashboard

import { apiClient, handleApiError } from './api';
import { ApiResponse } from '../types/api';

export interface KataCoachingSession {
  id?: number;
  workshop?: number;
  coach_mentor: number;
  mentees: number[];
  coaching_date: string;
  question_1_score?: number;
  question_2_score?: number;
  question_3_score?: number;
  question_4_score?: number;
  question_5_score?: number;
  question_6_score?: number;
  question_7_score?: number;
  question_8_score?: number;
  question_9_score?: number;
  question_10_score?: number;
  notes?: string;
  coaching_lead_time_minutes?: number;
  total_score?: number;
  total_score_percentage?: number;
  completion_rate?: number;
  created_at?: string;
  updated_at?: string;
  // List serializer fields (for table display)
  workshop_title?: string;
  workshop_number?: string;
  coach_name?: string;
  mentees_count?: number;
  mentees_names?: string[];
  can_edit?: boolean;
  // Nested details (for detailed view)
  workshop_details?: any;
  coach_details?: any;
  mentees_details?: any[];
}

export interface KataCoachingFilters {
  workshop?: number;
  coach_mentor?: number;
  mentees?: number;
  coaching_date_from?: string;
  coaching_date_to?: string;
  deadline_from?: string;
  deadline_to?: string;
  alv_id?: string;
  total_score_min?: number;
  total_score_max?: number;
  search?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface WorkshopWithUsers {
  id: number;
  title: string;
  workshop_number: string;
  mentor_user?: any;  // User object for mentor
  mentees_users?: any[];  // Array of User objects for mentees
  // ... other workshop fields
}

export class KataCoachingService {
  /**
   * Get kata coaching sessions with filtering and pagination
   */
  static async getSessions(filters: KataCoachingFilters = {}): Promise<ApiResponse<KataCoachingSession>> {
    try {
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (key === 'mentees' && Array.isArray(value)) {
            // Handle multiple mentees by joining them with commas
            params.append(key, value.join(','));
          } else {
            params.append(key, value.toString());
          }
        }
      });

      const response = await apiClient.get<ApiResponse<KataCoachingSession>>(`/kata-coaching-sessions/?${params}`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get a single kata coaching session by ID
   */
  static async getSession(id: number): Promise<KataCoachingSession> {
    try {
      const response = await apiClient.get<KataCoachingSession>(`/kata-coaching-sessions/${id}/`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Create a new kata coaching session
   */
  static async createSession(data: KataCoachingSession): Promise<KataCoachingSession> {
    try {
      const response = await apiClient.post<KataCoachingSession>('/kata-coaching-sessions/', data);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Update an existing kata coaching session
   */
  static async updateSession(id: number, data: Partial<KataCoachingSession>): Promise<KataCoachingSession> {
    try {
      const response = await apiClient.patch<KataCoachingSession>(`/kata-coaching-sessions/${id}/`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Delete a kata coaching session
   */
  static async deleteSession(id: number): Promise<void> {
    try {
      await apiClient.delete(`/kata-coaching-sessions/${id}/`);
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get coaching session statistics
   */
  static async getStats(): Promise<any> {
    try {
      const response = await apiClient.get('/kata-coaching-sessions/stats/');
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get recent coaching sessions
   */
  static async getRecentSessions(): Promise<KataCoachingSession[]> {
    try {
      const response = await apiClient.get<KataCoachingSession[]>('/kata-coaching-sessions/recent/');
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get workshop with users for auto-fill functionality
   */
  static async getWorkshopWithUsers(workshopId: number): Promise<WorkshopWithUsers> {
    try {
      const response = await apiClient.get<WorkshopWithUsers>(`/workshops/${workshopId}/`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Calculate total score from question scores
   */
  static calculateTotalScore(session: Partial<KataCoachingSession>): number {
    const scores = [
      session.question_1_score, session.question_2_score, session.question_3_score,
      session.question_4_score, session.question_5_score, session.question_6_score,
      session.question_7_score, session.question_8_score, session.question_9_score,
      session.question_10_score
    ];
    const validScores = scores.filter(score => score !== undefined && score !== null);
    return validScores.reduce((sum, score) => sum + score, 0);
  }

  /**
   * Calculate total score percentage (total_score/50)
   */
  static calculateTotalScorePercentage(session: Partial<KataCoachingSession>): number {
    const totalScore = this.calculateTotalScore(session);
    return totalScore / 50;
  }

  /**
   * Calculate completion rate based on answered questions
   */
  static calculateCompletionRate(session: Partial<KataCoachingSession>): number {
    const scores = [
      session.question_1_score, session.question_2_score, session.question_3_score,
      session.question_4_score, session.question_5_score, session.question_6_score,
      session.question_7_score, session.question_8_score, session.question_9_score,
      session.question_10_score
    ];
    const answeredQuestions = scores.filter(score => score !== undefined && score !== null).length;
    return (answeredQuestions / 10) * 100;
  }

  /**
   * Validate coaching session data
   */
  static validateSession(data: Partial<KataCoachingSession>): string[] {
    const errors: string[] = [];

    if (!data.workshop) {
      errors.push('Workshop is required');
    }

    if (!data.coach_mentor) {
      errors.push('Coach/Mentor is required');
    }

    if (!data.mentees || data.mentees.length === 0) {
      errors.push('At least one mentee is required');
    }

    if (!data.coaching_date) {
      errors.push('Coaching date is required');
    }

    // Check if at least one question is answered
    const questionFields = [
      'question_1_score', 'question_2_score', 'question_3_score',
      'question_4_score', 'question_5_score', 'question_6_score',
      'question_7_score', 'question_8_score', 'question_9_score',
      'question_10_score'
    ];

    const answeredQuestions = questionFields.filter(field => 
      data[field as keyof KataCoachingSession] !== undefined && 
      data[field as keyof KataCoachingSession] !== null
    );

    if (answeredQuestions.length === 0) {
      errors.push('At least one question must be answered');
    }

    // Validate score ranges
    questionFields.forEach(field => {
      const score = data[field as keyof KataCoachingSession] as number;
      if (score !== undefined && score !== null && (score < 0 || score > 5)) {
        errors.push(`${field} must be between 0 and 5`);
      }
    });

    return errors;
  }

  /**
   * Get attachments for a coaching session
   */
  static async getAttachments(sessionId: number): Promise<any[]> {
    try {
      const response = await apiClient.get<any[]>(`/kata-coaching-sessions/${sessionId}/attachments/`);
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Upload an attachment to a coaching session
   */
  static async uploadAttachment(sessionId: number, file: File): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('original_filename', file.name);

      const response = await apiClient.post<any>(
        `/kata-coaching-sessions/${sessionId}/upload_attachment/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Delete an attachment from a coaching session
   */
  static async deleteAttachment(sessionId: number, attachmentId: number): Promise<void> {
    try {
      await apiClient.delete(`/kata-coaching-sessions/${sessionId}/attachments/${attachmentId}/`);
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }
}
